import { useId } from 'react'
import type { AvatarConfigDto } from '@/api/hooks'
import { AVATAR_LAYERS, type AvatarSlot } from './registry'

/** Ordre de peinture des couches clippées au médaillon (le fond d'abord). */
const CLIPPED_ORDER: AvatarSlot[] = ['background', 'face', 'facialHair', 'hair', 'headgear', 'accessory']

/**
 * Portrait de capitaine composé par couches SVG depuis le registre cosmétique.
 * config null → médaillon générique (joueur jamais passé par la cabine).
 * Une clé inconnue est IGNORÉE (bundle plus vieux que le catalogue) plutôt
 * que de casser le rendu. Ids de gradients uniques par instance (useId).
 * Le cadre (frame) est peint HORS du groupe clippé : son anneau vit au-delà
 * du rayon 53 du médaillon et serait entièrement mangé par le clip.
 */
export function CaptainPortrait({ config, size = 96, className = '' }: {
  config: AvatarConfigDto | null
  size?: number
  className?: string
}) {
  const uid = useId().replace(/:/g, 'i')

  if (!config) {
    return (
      <svg width={size} height={size} viewBox="0 0 120 120" className={className} role="img" aria-label="Capitaine sans portrait" data-generic-medallion>
        <circle cx="60" cy="60" r="53" fill="#0F5B70" stroke="#C9A55C" strokeWidth="2.5" />
        <ellipse cx="60" cy="52" rx="16" ry="18.5" fill="#0A3E4F" />
        <path d="M 30 96 C 34 80 45 72 60 72 C 75 72 86 80 90 96" fill="#0A3E4F" />
      </svg>
    )
  }

  const renderLayer = (slot: AvatarSlot) => {
    const renderer = AVATAR_LAYERS[slot][config[slot] ?? '']
    return renderer ? renderer({ uid: `${uid}-${slot}` }) : null
  }

  /* Buste fixe « peint » : veste en gradient, revers éclairé/ombré,
     chemise claire au centre, 3 boutons or. Silhouette inchangée
     (col (57.5,74)-(62.5,74), épaules y≥78, ancre buste y≥74). */
  const bust = (
    <>
      <path d="M 20 108 C 23 91 34 82 47 78.5 C 51 77 55.5 75.5 57.5 74 L 62.5 74 C 65 76 70 78 76 79.5 C 88 83 95 92 97 108 Z" fill={`url(#${uid}-coat)`} />
      {/* épaule gauche éclairée + rim doré discret côté droit */}
      <path d="M 26.5 95 C 31 87.5 38.5 82.9 47 80.7 C 39.5 84.7 33.5 89.9 29.6 97.6 Z" fill="#9A5629" opacity="0.7" />
      <path d="M 78.5 81.4 C 87.6 84.8 93.2 91.2 95.6 100.8 C 92.2 93.6 86.3 87.7 77.6 84.3 Z" fill="#E5CC8F" opacity="0.7" />
      {/* chemise claire au centre + modelé + ombre du col */}
      <path d="M 55.6 75 C 57.5 77.8 62.5 77.8 64.4 75 C 65.2 80.8 64.4 87.2 62.3 91.4 L 57.7 91.4 C 55.6 87.2 54.8 80.8 55.6 75 Z" fill="#F5EDD7" />
      <path d="M 56.5 80.2 C 58.6 81.6 61.4 81.6 63.5 80.2 C 63.4 82.6 63.1 84.9 62.5 86.9 C 60.9 87.9 59.1 87.9 57.5 86.9 C 56.9 84.9 56.6 82.6 56.5 80.2 Z" fill="#C9A55C" opacity="0.4" />
      <path d="M 55.8 75.2 C 58 76.9 62 76.9 64.2 75.2 C 63 78.4 57 78.6 55.8 75.2 Z" fill="#8A5A34" opacity="0.4" />
      {/* revers gauche clair / revers droit ombré + liserés or */}
      <path d="M 49.4 93 C 49.4 83.5 51.2 77.4 55.4 74.3 L 59 76.2 C 56.2 79.6 55.1 85.4 55 93.6 Z" fill="#8A4A26" />
      <path d="M 70.6 93.8 C 70.6 84 68.5 77.7 64.6 74.4 L 61 76.3 C 63.8 79.8 64.9 85.9 65 94.4 Z" fill="#46200E" />
      <path d="M 55.4 74.3 C 52.7 77.7 51.6 83.6 51.5 93.2" fill="none" stroke="#C9A55C" strokeWidth="0.9" opacity="0.85" />
      <path d="M 64.6 74.4 C 67.3 77.9 68.4 84 68.5 94" fill="none" stroke="#C9A55C" strokeWidth="0.9" opacity="0.6" />
      {/* 3 boutons or avec point de lumière */}
      <g fill="#E5CC8F" stroke="#5A2A12" strokeWidth="0.5">
        <circle cx="51" cy="90" r="1.7" />
        <circle cx="50" cy="97.5" r="1.7" />
        <circle cx="49.2" cy="105" r="1.7" />
      </g>
      <circle cx="50.4" cy="89.4" r="0.55" fill="#F7EFDE" />
      <circle cx="49.4" cy="96.9" r="0.55" fill="#F7EFDE" />
      <circle cx="48.6" cy="104.4" r="0.55" fill="#F7EFDE" />
    </>
  )

  const frame = renderLayer('frame')

  return (
    <svg width={size} height={size} viewBox="0 0 120 120" className={className} role="img" aria-label="Portrait du capitaine">
      <defs>
        <clipPath id={`${uid}-clip`}>
          <circle cx="60" cy="60" r="53" />
        </clipPath>
        <linearGradient id={`${uid}-ring`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F0DCA8" />
          <stop offset="1" stopColor="#A5813F" />
        </linearGradient>
        <linearGradient id={`${uid}-coat`} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stopColor="#7A3E1F" />
          <stop offset="1" stopColor="#431F0D" />
        </linearGradient>
      </defs>
      {/* Anneau du médaillon : cercle externe sombre + anneau or en gradient.
          ⚠️ Rester ≤ r56.6 : les frames (r55.4+) se peignent PAR-DESSUS. */}
      <circle cx="60" cy="60" r="56" fill="none" stroke="#4A2210" strokeWidth="1.2" />
      <circle cx="60" cy="60" r="54.6" fill="none" stroke={`url(#${uid}-ring)`} strokeWidth="2.8" />
      <g clipPath={`url(#${uid}-clip)`}>
        {CLIPPED_ORDER.map((slot) => (
          <g key={slot}>
            {/* Le buste est peint entre le visage et la pilosité pour que le
                col passe devant le cou mais derrière la barbe. */}
            {slot === 'facialHair' ? bust : null}
            {renderLayer(slot)}
          </g>
        ))}
      </g>
      {/* filet interne clair, par-dessus le bord du contenu clippé */}
      <circle cx="60" cy="60" r="53.15" fill="none" stroke="#F0DCA8" strokeWidth="0.8" opacity="0.5" />
      {frame ? <g>{frame}</g> : null}
    </svg>
  )
}
